import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  Divider,
  Button,
  DialogActions,
  FormGroup,
  FormControlLabel,
  DialogTitle,
  DialogContentText,
  Popover,
  CircularProgress,
} from "@mui/material";
import { BlockRounded, CheckOutlined, ListRounded } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CloseIcon from "@mui/icons-material/Close";
import TextDisplay from "./TextDisplay";
import { IOSSwitch } from "./IOSSwitch";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  formatDateIgnoreTimezone,
  formatDateTime,
} from "../../../utils/DateUtils";
import DriverEditDialog from "./DriverEditDialog";
import AddAssociationDialog from "./AddAssociationDialog";
import { driverGrid } from "../style";
import { v4 as uuidv4 } from "uuid";
import { useBusinessProfile } from "../../../utils/hooks/useBusinessProfile";
import { useAuth } from "../../../utils/hooks/useAuth";
import { fleetService, CheckDriverOnboardingPayload, CheckDriverOnboardingResponse, UpdateDriverVehicleAssociationStatusPayload } from "../../../api/fleetService/fleetServicesRegistered";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import CustomPopover from "./CustomPopover";
import VehicleTypeFilter from "./VehicleTypeFilter";
import AssociationVehicleTypeFilter from "./AssociationVehicleTypeFilter";
import AssociationStatusFilter from "./AssociationStatusFilter";
import VehicleLicensePlateFilter from "./VehicleLicensePlateFilter";
import AssociationPeriodFilter from "./AssociationPeriodFilter";

const DriverCellRenderer: React.FC<{
  data: any;
  setDriverDataList: React.Dispatch<React.SetStateAction<any>>;
  vehiclesList: any;
  driverDataList: any;
  onOpenDialogDriverStatus: (phone: string) => void;
}> = ({
  data,
  setDriverDataList,
  vehiclesList,
  driverDataList,
  onOpenDialogDriverStatus,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [driver, setDriver] = useState(data);
  const [openAssociationDialog, setOpenAssociationDialog] = useState(false);
  const { businessProfile } = useBusinessProfile();
  const { user } = useAuth();
  const [selectedAssociations, setSelectedAssociations] = useState<any>(null);
  const [localAssociations, setLocalAssociations] = useState(data.associations);
  const [hasChanges, setHasChanges] = useState(false);
  const [openDialogDriverStatus, setOpenDialogDriverStatus] = useState(false);
  const [selectedLicensePlate, setSelectedLicensePlate] = useState<
    string | null
  >(null);
  const [selectedAssociationId, setSelectedAssociationId] = useState<
    number | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const [errorPerform, setErrorPerform] = useState<string | null>(null);
  const [performResult, setPerformResult] = useState<CheckDriverOnboardingResponse | null>(null);
  const [localDriver, setLocalDriver] = useState(data);
  const [loadingOnboardingRequest, setLoadingOnboardingRequest] = useState(false);
  const [errorOnboardingRequest, setErrorOnboardingRequest] = useState<string | null>(null);
  const [shouldSync, setShouldSync] = useState(false);

  const handleNewAssociation = (association: any) => {
    setSelectedAssociations([
      {
        vehicleLicensePlate: "",
        vehicleSubType: "",
        vehicleType: "TRACTOR",
        dateFrom: null,
        dateTo: null,
        status: "ACTIVE",
        lastAssociationUpdate: new Date().toISOString(),
      },
    ]);
    setOpenAssociationDialog(true);
  };

  const handleEditAssociation = (association: any) => {
    setSelectedAssociations([association]);
    setOpenAssociationDialog(true);
  };

  const handleInsertAssociations = (newAssociations: any) => {
    const updatedAssociations = [
      ...localAssociations,
      ...newAssociations.map((assoc: any) => ({
        ...assoc,
        id: assoc.id || uuidv4(),
      })),
    ];
    setLocalAssociations(updatedAssociations);
    setHasChanges(true);
    setOpenAssociationDialog(false);
  };

  const handleUpdateAssociations = (updatedAssociations: any) => {
    const newAssociations = localAssociations.map((existingAssoc: any) => {
      const match = updatedAssociations.find(
        (updatedAssoc: any) => updatedAssoc.id === existingAssoc.id
      );
      return match
        ? { ...existingAssoc, dateFrom: match.dateFrom, dateTo: match.dateTo }
        : existingAssoc;
    });
    setLocalAssociations(newAssociations);
    setHasChanges(true);
    setOpenAssociationDialog(false);
  };

  const handleStatusChangeAssociation = async (associationId: number, licensePlate: string) => {
    if (!businessProfile) return;

    try {
      const association = localAssociations.find((a: any) => a.id === associationId);
      if (!association) return;

      const newStatus = association.status === "ACTIVE" ? "CANCELLED" : "ACTIVE";
      const editUser = user?.email || "";
      const payload: UpdateDriverVehicleAssociationStatusPayload = {
        legacyCodes: {
          legacyCode: businessProfile.legacycodes[0].legacycode,
          company: businessProfile.legacycodes[0].company,
        },
        id: associationId,
        licensePlate: licensePlate,
        status: newStatus,
        editUser: editUser,
      };
      
      await fleetService.updateDriverVehicleAssociationStatus(payload);

      const updatedAssociations = localAssociations.map((a: any) =>
        a.id === associationId ? { ...a, status: newStatus } : a
      );
      
      setLocalAssociations(updatedAssociations);
      setDriver((prev: any) => ({
        ...prev,
        associations: updatedAssociations
      }));

      setHasChanges(true);
      toast.success(t("screens.fleetAndRoute.toast.associationStatusChanged"));
    } catch (error: any) {
      console.error("Errore nel cambio di stato della relazione autista-mezzo", error);
      toast.error(t("screens.fleetAndRoute.toast.associationStatusChangeError"));
      
      if (error.response?.data?.tripInfo && error.response.data.tripInfo.length > 0) {
        let errorMessage = (
          <>
            <Typography sx={{ fontSize: "14px" }}>
             
              {t('screens.fleetAndRoute.errorDialog.vehicleRemoval', { plate:licensePlate })}
            </Typography>
            {error.response.data.tripInfo.map(
              (trip: { tripID: number; tmsObjectNumber: string }) => (
                <Box
                  key={trip.tripID}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    columnGap: "4px",
                  }}
                >
                  <Box
                    sx={{
                      width: "6px",
                      height: "6px",
                      backgroundColor: "#333333",
                      borderRadius: "100%",
                    }}
                  />
                  <Button
                    onClick={() => handleClick(trip.tripID)}
                    variant="text"
                    color="primary"
                    style={{
                      textDecoration: "underline",
                      color: "#333333",
                      textTransform: "none",
                      fontSize: "14px",
                      padding: '0'
                    }}
                  >
                    Trip ID: {trip.tmsObjectNumber}
                  </Button>
                </Box>
              )
            )}
          </>
        );
        setErrorMessage(errorMessage);
        setPopoverAnchor(document.getElementById(`target-element-${associationId}`) as HTMLElement);
      }
    }
  };

  const handleStatusChangeDriver = async () => {
    if (!businessProfile) return;

    try {
      const newStatus = driver.status === "ACTIVE" ? "CANCELLED" : "ACTIVE";
      const editUser = user?.email || "";

      await fleetService.updateDriverStatus(
        businessProfile.legacycodes[0].legacycode,
        businessProfile.legacycodes[0].company,
        driver.phone,
        newStatus,
        editUser
      );

      setDriver((prev: any) => ({
        ...prev,
        status: newStatus
      }));
      setHasChanges(true);
      toast.success(t("screens.fleetAndRoute.toast.driverStatusChanged"));
    } catch (error: any) {
      console.error("Errore nel cambio di stato dell'autista", error);
      toast.error(t("screens.fleetAndRoute.toast.driverStatusChangeError"));
      let errorMessage = (
        <>
          {error.response?.data?.tripInfo?.length > 0 && (
            <>
              <Typography sx={{ fontSize: "14px" }}>
              
                {t('screens.fleetAndRoute.errorDialog.driverRemoval', { name:driver.phone })}
              </Typography>
              {error.response.data.tripInfo.map(
                (trip: { tripID: number; tmsObjectNumber: string }) => (
                  <Box
                    key={trip.tripID}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      columnGap: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#333333",
                        borderRadius: "100%",
                      }}
                    />
                    <Button
                      onClick={() => handleClick(trip.tripID)}
                      variant="text"
                      color="primary"
                      style={{
                        textDecoration: "underline",
                        color: "#333333",
                        textTransform: "none",
                        fontSize: "14px",
                        padding: '0'
                      }}
                    >
                      Trip ID: {trip.tmsObjectNumber}
                    </Button>
                  </Box>
                )
              )}
            </>
          )}
        </>
      );
      setErrorMessage(errorMessage);
      setPopoverAnchor(document.getElementById(`target-element-${driver.phone}`) as HTMLElement);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setShouldSync(true);
    }
    setOpen(false);
  };

  const columnDefsDrivers = useMemo(
    () => [
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.plate"),
        field: "vehicleLicensePlate",
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.plate").toString(),
        tooltipField: "vehicleLicensePlate",
        width: 120,
        filter: VehicleLicensePlateFilter,
        filterParams: {
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          return (
            <Button
              onClick={() => handleEditAssociation(params.data)}
              sx={{
                textTransform: "none",
                color: "#333333",
                textDecoration: "underline",
              }}
            >
              {params.value}
            </Button>
          );
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.vehicleType"),
        field: "vehicleType",
        headerTooltip: t(
          "screens.fleetAndRoute.gridDrivers.vehicleType"
        ).toString(),
        tooltipValueGetter: (params: any) => {
          const typeKey = params.data?.vehicleType?.trim() || "-";
          const subTypeKey = params.data?.vehicleSubType?.trim() || "-";
          const type = t(`screens.fleetAndRoute.vehicles.${typeKey}`, typeKey);
          const subType = t(`screens.fleetAndRoute.vehicles.${subTypeKey}`, subTypeKey);
          return `${type} - ${subType}`;
        },
        flex: 1,
        maxWidth: 160,
        cellRenderer: (params: any) => {
          const typeKey = params.data?.vehicleType?.trim() || "-";
          const subTypeKey = params.data?.vehicleSubType?.trim() || "-";
          const type = t(`screens.fleetAndRoute.vehicles.${typeKey}`, typeKey);
          const subType = t(`screens.fleetAndRoute.vehicles.${subTypeKey}`, subTypeKey);
          return (
            <Box sx={driverGrid.vehicleTypeBox}>
              <Typography style={driverGrid.vehicleTypeText}>
                {type.toString()} - {subType.toString()}
              </Typography>
            </Box>
          );
        },
        filter: AssociationVehicleTypeFilter,
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.associationPeriod"),
        headerTooltip: t(
          "screens.fleetAndRoute.gridDrivers.associationPeriod"
        ).toString(),
        filter: 'AssociationPeriodFilter',
        filterParams: {
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        tooltipValueGetter: (params: any): string => {
          const parsedDateFrom = params.data?.dateFrom
            ? new Date(params.data.dateFrom.replace("T", " "))
                .toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                .replace(",", "")
            : "-";

          const parsedDateTo = params.data?.dateTo
            ? new Date(params.data.dateTo.replace("T", " "))
                .toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                .replace(",", "")
            : "-";

          return ` ${parsedDateFrom} - ${parsedDateTo}`;
        },
        field: "dateFrom",
        width: 260,
        cellRenderer: (params: any) => {
          const parsedDateFrom = params.data?.dateFrom
            ? new Date(params.data.dateFrom.replace("T", " "))
                .toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                .replace(",", "")
            : "-";

          const parsedDateTo = params.data?.dateTo
            ? new Date(params.data.dateTo.replace("T", " "))
                .toLocaleString(undefined, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                .replace(",", "")
            : "-";

          return (
            <Box sx={driverGrid.dataBox}>
              <Typography style={driverGrid.dataText}>
                {parsedDateFrom} - {parsedDateTo}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.status"),
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.status").toString(),
        field: "status",
        width: 110,
        tooltipValueGetter: (params: any): string => {
          return params.data?.status === "ACTIVE"
            ? t("screens.fleetAndRoute.status.ACTIVE")
            : t("screens.fleetAndRoute.status.CANCELLED");
        },
        valueFormatter: (params: any) => {
          if (params.value === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.value === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.value;
        },
        filterValueGetter: (params: any) => {
          if (params.data?.status === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.data?.status === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.data?.status;
        },
        filter: AssociationStatusFilter,
        cellRenderer: (params: any) => {
          return (
            <FormGroup sx={driverGrid.statusForm}>
              <FormControlLabel
                control={
                  <IOSSwitch
                    id={`target-element-${params.data.id}`}
                    sx={{ m: 1 }}
                    checked={params.data.status === "ACTIVE"}
                    onChange={() =>
                      handleOpenDialogDriverStatus(
                        params.data.vehicleLicensePlate,
                        params.data.id
                      )
                    }
                  />
                }
                label=""
              />
            </FormGroup>
          );
        },
      },
      {
        headerName: t(
          "screens.fleetAndRoute.gridDrivers.lastAssociationUpdate"
        ),
        headerTooltip: t(
          "screens.fleetAndRoute.gridDrivers.lastAssociationUpdate"
        ).toString(),
        tooltipValueGetter: (params: any): string => {
          const dateString = params.data?.lastAssociationUpdate;
          if (!dateString) return "-";

          const parsedDate = parseISO(dateString);
          if (!isValid(parsedDate)) return "-";

          return formatDateTime(dateString);
        },
        field: "lastAssociationUpdate",
        flex: 1,
        filter: "agDateColumnFilter",
        valueGetter: (params: any) => {
          const dateString = params.data?.lastAssociationUpdate;
          if (!dateString) return null;
          const date = new Date(dateString);
          return isNaN(date.getTime()) ? null : date;
        },
        filterParams: {
          browserDatePicker: true,
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (!cellValue) return -1;

            const cellDate = new Date(cellValue);
            const filterDate = new Date(filterLocalDateAtMidnight);

            cellDate.setHours(0, 0, 0, 0);
            filterDate.setHours(0, 0, 0, 0);

            if (cellDate < filterDate) return -1;
            if (cellDate > filterDate) return 1;
            return 0;
          },
        },
        cellRenderer: (params: any) => {
          const dateString = params.data?.lastAssociationUpdate;
          if (!dateString) return <Box sx={{display:"flex",flexDirection:"row",justifyContent:"flex-start",alignItems:"center",height:"40px"}}>-</Box>;

          const parsedDate = parseISO(dateString);
          if (!isValid(parsedDate)) return "-";

          return <Box sx={{display:"flex",flexDirection:"row",justifyContent:"flex-start",alignItems:"center",height:"40px"}}>{formatDateTime(dateString)}</Box>;
        },
      },
    ],
    [driverDataList, driver, selectedAssociations]
  );
  const gridRef = useRef<any>(null);
  const onGridReady = () => {
    if (gridRef.current) {
      gridRef.current.api.setDomLayout("autoHeight");
    }
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpenDialogDriverStatus = (
    licensePlate: string,
    associationId?: number
  ) => {
    setSelectedLicensePlate(licensePlate);
    setSelectedAssociationId(associationId ?? null);
    setOpenDialogDriverStatus(true);
  };

  const handleCloseDialogDriverStatus = () => {
    setOpenDialogDriverStatus(false);
    setSelectedLicensePlate(null);
    setSelectedAssociationId(null);
  };

  const handleConfirmStatusDriverChange = async () => {
    if (selectedLicensePlate) {
      await handleStatusChangeAssociation(
        selectedAssociationId ?? -1,
        selectedLicensePlate
      );
    } else {
      await handleStatusChangeDriver();
    }
    handleCloseDialogDriverStatus();
  };

  const navigate = useNavigate();
  const handleClick = (id: number) => {
    window.open(`/tripdetails/${id}`, "_blank", "noopener,noreferrer");
  };

    const handleCheckOnboarding = async (driverPhone: string) => {
    if (loadingRows[driverPhone]) return;

    setLoadingRows((prev) => ({ ...prev, [driverPhone]: true }));
    setErrorPerform(null);

    try {
      const payload: CheckDriverOnboardingPayload = {
        legacyCodes: {
          legacyCode: businessProfile?.legacycodes[0].legacycode ?? "",
          company: businessProfile?.legacycodes[0].company ?? "",
        },
        phoneNumber: driverPhone,
      };

      const response = await fleetService.checkDriverOnboarding(payload);
      setPerformResult(response);
      
      if (response.result === "OK") {
        setLocalDriver((prev: any) => ({
          ...prev,
          onboardingDateVerify: new Date().toISOString(),
          onboardingStatus: "Verified"
        }));
        toast.success("Verifica onboarding effettuata con successo");
      } else {
        setLocalDriver((prev: any) => ({
          ...prev,
          onboardingDateVerify: new Date().toISOString(),
          onboardingStatus: "Ongoing"
        }));
        toast.warning("Verifica onboarding effettuata: Non verificato");
      }
      setHasChanges(true);
    } catch (err) {
      setErrorPerform("Errore nel controllo dell'onboarding");
      toast.error("Errore durante la verifica dell'onboarding.");
    } finally {
      setLoadingRows((prev) => ({ ...prev, [driverPhone]: false }));
    }
  };

  const handleRequestOnboarding = async (driverPhone: string) => {
    setLoadingOnboardingRequest(true);
    setErrorOnboardingRequest(null);

    const payload: CheckDriverOnboardingPayload = {
      legacyCodes: {
        legacyCode: businessProfile?.legacycodes[0].legacycode ?? "",
        company: businessProfile?.legacycodes[0].company ?? "",
      },
      phoneNumber: driverPhone,
    };

    try {
      await fleetService.requestDriverOnboarding(payload);
      
      setDriverDataList((prevList: any) =>
        prevList.map((driver: any) =>
          driver.phone === driverPhone
            ? { ...driver, onboardingStatus: "Ongoing" }
            : driver
        )
      );
      toast.success("Richiesta di onboarding inviata con successo!");
    } catch (err) {
      setErrorOnboardingRequest("Errore durante la richiesta di onboarding.");
      console.error(err);
      toast.error("Errore durante la richiesta di onboarding.");
    } finally {
      setLoadingOnboardingRequest(false);
    }
  };

  useEffect(() => {
  }, [localDriver]);

  useEffect(() => {
    if (shouldSync && hasChanges) {
      setDriverDataList((prev: any) =>
        prev.map((d: any) =>
          d.phone === data.phone
            ? {
                ...d,
                associations: localAssociations,
                status: driver.status,
                onboardingStatus: driver.onboardingStatus,
                onboardingDateVerify: driver.onboardingDateVerify
              }
            : d
        )
      );
      setShouldSync(false);
      setHasChanges(false);
    }
  }, [shouldSync, hasChanges, data.phone, localAssociations, driver, setDriverDataList]);

  return (
    <>
      {errorMessage && (
        <CustomPopover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
        >
          {errorMessage}
        </CustomPopover>
      )}
      <Box
        sx={{
          ...driverGrid.mainBox,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={() => setOpen(true)}
      >
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {data.firstName} {data.lastName}
        </Box>
        {data.associations.length > 1 && (
          <IconButton sx={driverGrid.iconButton}>
            <ListRounded sx={driverGrid.icon} fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <Box sx={driverGrid.dialogBox}>
          <Typography sx={driverGrid.dialogText}>
            {t("screens.fleetAndRoute.headerDriver.driver")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ padding: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <DialogContent>
          <Box sx={driverGrid.dialogContent}>
            <Box>
              <Typography sx={driverGrid.generalData}>
                {t("screens.fleetAndRoute.headerDriver.generalData")}
              </Typography>
              <Box sx={driverGrid.contentSubWrapper}>
                <TextDisplay
                  firstText={t("screens.fleetAndRoute.headerDriver.name")}
                  secondText={driver.firstName}
                />
                <TextDisplay
                  firstText={t("screens.fleetAndRoute.headerDriver.surname")}
                  secondText={driver.lastName}
                />
                <TextDisplay
                  firstText={t("screens.fleetAndRoute.headerDriver.lastLogin")}
                  secondText={data.lastAccess}
                />
                <TextDisplay
                  firstText={t("screens.fleetAndRoute.headerDriver.phone")}
                  secondText={driver.phone}
                />
                <TextDisplay
                  firstText={t("screens.fleetAndRoute.headerDriver.email")}
                  secondText={driver.email}
                />
               
              </Box>
              <Box sx={driverGrid.contentSubWrapper}>
                <Box sx={driverGrid.box1}>
                  <Typography sx={driverGrid.text}>
                    {t("screens.fleetAndRoute.headerDriver.driverStatus")}
                  </Typography>
                  <IOSSwitch
                    checked={localDriver.status === "ACTIVE"}
                    onChange={() => onOpenDialogDriverStatus(localDriver.phone)}
                  />
                </Box>
         
           
              </Box>
              <Divider sx={{ marginBottom: "16px" }} />
              <Typography sx={driverGrid.text1}>
                {t("screens.fleetAndRoute.headerDriver.onboarding")}
              </Typography>
              <Box sx={driverGrid.box2}>
                <Box sx={driverGrid.box3}>
                  <Typography sx={driverGrid.text2}>
                    {t("screens.fleetAndRoute.headerDriver.appOnboardingCheck")}
                  </Typography>
                  {localDriver.onboardingStatus === "Verified" ? (
                    <Box sx={driverGrid.box4}>
                      {" "}
                      <CheckOutlined sx={{ color: "green" }} />
                      <Typography sx={{ fontSize: "14px" }}>
                        {localDriver.onboardingDateVerify ? formatDateTime(localDriver.onboardingDateVerify) : "-"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={driverGrid.box5}>
                      {" "}
                      <BlockRounded sx={{ color: "#CF0A2C" }} />
                      <Typography sx={{ fontSize: "14px" }}>
                        {localDriver.onboardingDateVerify ? formatDateTime(localDriver.onboardingDateVerify) : "-"}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={driverGrid.box6}>
                  {loadingRows[localDriver.phone] ? (
                    <CircularProgress size={24} sx={{ marginTop: "8px", color: "#CF0A2C" }} />
                  ) : (
                    <Button 
                      variant="contained" 
                      sx={driverGrid.button}
                      onClick={() => handleCheckOnboarding(localDriver.phone)}
                    >
                      {t("screens.fleetAndRoute.headerDriver.checkOnboarding")}
                    </Button>
                  )}
                  {(localDriver.onboardingStatus === "Verified" ||
                    localDriver.onboardingStatus === "Request") && (
                    <Button
                      variant="contained"
                      disabled={localDriver.onboardingStatus === "Verified" || loadingOnboardingRequest}
                      sx={driverGrid.button}
                      onClick={() => handleRequestOnboarding(localDriver.phone)}
                    >
                      {loadingOnboardingRequest ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        t("screens.fleetAndRoute.headerDriver.requestOnboarding")
                      )}
                    </Button>
                  )}
                  {localDriver.onboardingStatus === "Ongoing" && (
                    <Button
                      variant="contained"
                      disabled={localDriver.onboardingStatus === "Ongoing"}
                      sx={driverGrid.button}
                    >
                      {t("screens.fleetAndRoute.headerDriver.onGoing")}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
            <div
              className="ag-theme-alpine"
              style={{ height: "auto", width: "100%" }}
            >
              <AgGridReact
                rowData={localAssociations}
                columnDefs={columnDefsDrivers}
                domLayout="autoHeight"
                onGridReady={onGridReady}
                defaultColDef={{
                  resizable: true,
                  filter: true,
                  floatingFilter: true,
                  suppressFiltersToolPanel: false,
                  sortable: true,
                }}
                frameworkComponents={{
                  AssociationPeriodFilter: AssociationPeriodFilter
                }}
                suppressDragLeaveHidesColumns={true}
                enableBrowserTooltips={true}
                enableCellTextSelection={true}  // ✅ Selezione del testo nella cella
                suppressClipboardApi={true} 
              />
            </div>
          </Box>
        </DialogContent>
        <DialogActions sx={{padding:3}}>
          <Button
            variant="outlined"
            sx={{ color: "#333333", borderColor: "#333333" }}
            onClick={() => setIsDialogOpen(true)}
          >
            {t("screens.fleetAndRoute.headerDriver.editDriver")}
          </Button>
          <Button
            onClick={() => handleNewAssociation("")}
            sx={{ backgroundColor: "#CF0A2C" }}
            variant="contained"
            disabled={driver.status === "CANCELLED" ? true : false}
          >
            {t("screens.fleetAndRoute.headerDriver.addAssociation")}
          </Button>
        </DialogActions>
      </Dialog>
      <DriverEditDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        driver={driver}
        setDriver={setDriver}
        setDriverDataList={setDriverDataList}
      />
      <AddAssociationDialog
        vehicleGridData={vehiclesList}
        open={openAssociationDialog}
        onClose={() => setOpenAssociationDialog(false)}
        onInsert={handleInsertAssociations}
        onUpdate={handleUpdateAssociations}
        selectedAssociations={selectedAssociations}
        driver={driver}
      />
      <Dialog
        sx={{ zIndex: 9999 }}
        open={openDialogDriverStatus}
        onClose={handleCloseDialogDriverStatus}
      >
        <DialogTitle>{t("screens.fleetAndRoute.handleConfirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("screens.fleetAndRoute.handleConfirm.text2")}
            {selectedAssociationId
              ? t("screens.fleetAndRoute.handleConfirm.text3")
              : t("screens.fleetAndRoute.handleConfirm.text4")}
          </DialogContentText>
        </DialogContent>
        <DialogActions >
          <Button
            variant="outlined"
            onClick={handleCloseDialogDriverStatus}
            sx={{ color: "#333333", borderColor: "#333333" }}
          >
            {t("screens.fleetAndRoute.headerDriver.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStatusDriverChange}
            sx={{ backgroundColor: "#CF0A2C" }}
            autoFocus
          >
            {t("screens.fleetAndRoute.headerDriver.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DriverCellRenderer;
